/**
 * Apex Auth Client — BFF authentication helpers for the SPA.
 *
 * Tokens live in HTTP-only cookies managed by the server.
 * The only client-readable cookie is apex_user_info (base64url JSON).
 */

/* eslint-disable local-rules/no-raw-fetch -- this module defines apiFetch; it must use raw fetch */

import type { User } from './lib/types';
import { trace, debugEmit } from './lib/debug-stream';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
  return match ? (match[1] ?? null) : null;
}

function parseUserInfo(): User | null {
  const raw = getCookie('apex_user_info');
  if (!raw) return null;
  try {
    // base64url -> base64 -> decode
    let b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

let cachedInfo: User | null = null;

/** Get user info from the SPA-readable cookie. Returns { email, name, role, sub } or null. */
export function getUserInfo(): User | null {
  if (!cachedInfo) cachedInfo = parseUserInfo();
  return cachedInfo;
}

/** True if the Keycloak auth cookie is present. */
export function isAuthenticated(): boolean {
  return getCookie('apex_user_info') !== null;
}

/** Redirect to Keycloak login (with loop guard). */
export function login(returnTo?: string): void {
  debugEmit('auth', 'login redirect', { returnTo });
  // Prevent redirect loops: if we've redirected to login more than 3 times
  // in 30 seconds, stop and show an error instead of freezing the tab.
  const now = Date.now();
  const key = 'apex_login_redirect_ts';
  const countKey = 'apex_login_redirect_count';
  const lastTs = parseInt(sessionStorage.getItem(key) || '0', 10);
  const count = parseInt(sessionStorage.getItem(countKey) || '0', 10);

  if (now - lastTs < 30000) {
    if (count >= 3) {
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(countKey);
      document.body.innerHTML =
        '<div style="padding:40px;font-family:system-ui;max-width:500px;margin:0 auto;text-align:center">' +
        '<h2>Session Error</h2>' +
        '<p>Unable to establish a valid session. This usually means your organization access has changed.</p>' +
        '<p><a href="/auth/logout" style="color:#2E90FA">Sign out and try again</a></p>' +
        '</div>';
      return;
    }
    sessionStorage.setItem(countKey, String(count + 1));
  } else {
    sessionStorage.setItem(key, String(now));
    sessionStorage.setItem(countKey, '1');
  }

  const dest = returnTo || window.location.pathname + window.location.search;
  window.location.href = '/auth/login?return_to=' + encodeURIComponent(dest);
}

/** Log out: clear server cookies + localStorage, kill Keycloak SSO session. */
export async function logout(): Promise<void> {
  const t = trace('auth', 'logout');
  // Clear email-based session data
  cachedInfo = null;
  localStorage.removeItem('apex_user_email');
  localStorage.removeItem('apex_rep_email');
  localStorage.removeItem('console_rep_email');
  localStorage.removeItem('inbox_rep_email');

  try {
    const res = await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    if (res.ok) {
      const data = await res.json();
      if (data.keycloak_logout_url) {
        t.ok({ redirect: 'keycloak' });
        window.location.href = data.keycloak_logout_url;
        return;
      }
    }
  } catch {
    // Fall through to basic redirect
  }

  // Fallback: redirect to login page (forces re-auth)
  t.ok({ redirect: 'fallback' });
  window.location.href = '/auth/login';
}

/** Refresh the access token via server-side refresh. Returns true on success.
 *  Deduplicates concurrent calls — only one refresh request is in-flight at a time. */
let refreshPromise: Promise<boolean> | null = null;

export function refresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  const t = trace('auth', 'refresh');
  refreshPromise = fetch('/auth/refresh', { method: 'POST', credentials: 'same-origin' })
    .then((res) => {
      if (res.ok) {
        cachedInfo = null; // bust cache so next getUserInfo() re-reads cookie
        t.ok();
        return true;
      }
      t.ok({ success: false, status: res.status });
      return false;
    })
    .catch((err) => {
      t.fail(err);
      return false;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

/** Switch the active organization. Returns true on success, reloads the page. */
export async function switchOrg(organizationId: string): Promise<boolean> {
  const t = trace('auth', 'switchOrg', { organizationId });
  try {
    const res = await fetch('/auth/switch-org', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organizationId }),
    });
    if (res.ok) {
      cachedInfo = null;
      t.ok();
      window.location.reload();
      return true;
    }
    t.ok({ success: false });
    return false;
  } catch (err) {
    t.fail(err);
    return false;
  }
}

/**
 * Fetch wrapper with automatic 401 retry.
 * On 401: attempts refresh, retries once, then redirects to login.
 */
export function apiFetch(url: string, opts?: RequestInit): Promise<Response> {
  const options: RequestInit = { ...opts, credentials: 'same-origin' };

  return fetch(url, options).then((res) => {
    if (res.status !== 401) return res;

    // Try refresh
    return refresh().then((ok) => {
      if (!ok) {
        login();
        return res;
      }
      return fetch(url, options);
    });
  });
}

// Backward compat — onclick handlers in innerHTML reference window.ApexAuth
declare global {
  interface Window {
    ApexAuth: typeof ApexAuth;
  }
}

const ApexAuth = { getUserInfo, isAuthenticated, login, logout, refresh, apiFetch, switchOrg };
// Guarded so importing this module doesn't crash in non-browser contexts
// (vitest node environment reaches it via lib/api → lib/permissions).
if (typeof window !== 'undefined') {
  window.ApexAuth = ApexAuth;
}
